"use client";

import React from "react";
import { cn } from "../utils";

export interface TireSpinnerProps {
  /** Size of the spinner */
  size?: "sm" | "md" | "lg" | "xl";
  /** Additional class names */
  className?: string;
  /** Optional label text below the spinner */
  label?: string;
}

const sizeMap = {
  sm: "w-8 h-8",
  md: "w-12 h-12",
  lg: "w-16 h-16",
  xl: "w-24 h-24",
};

const labelSizeMap = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
  xl: "text-lg",
};

/**
 * Tire-themed loading spinner.
 *
 * Renders an SVG tire/wheel that spins with a smooth CSS animation.
 * Use across all RentOS apps for a consistent branded loading experience.
 *
 * @example
 * <TireSpinner size="lg" label="Loading bookings..." />
 */
export function TireSpinner({ size = "md", className, label }: TireSpinnerProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
      <svg
        className={cn(sizeMap[size], "animate-spin-tire text-primary")}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Outer tire */}
        <circle
          cx="32"
          cy="32"
          r="28"
          stroke="currentColor"
          strokeWidth="6"
          opacity="0.3"
        />
        {/* Tire tread (outer ring) */}
        <circle
          cx="32"
          cy="32"
          r="28"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray="20 12"
        />
        {/* Inner rim */}
        <circle
          cx="32"
          cy="32"
          r="16"
          stroke="currentColor"
          strokeWidth="3"
          opacity="0.6"
        />
        {/* Hub */}
        <circle cx="32" cy="32" r="5" fill="currentColor" opacity="0.8" />
        {/* Spokes */}
        <line x1="32" y1="17" x2="32" y2="27" stroke="currentColor" strokeWidth="2" opacity="0.5" />
        <line x1="32" y1="37" x2="32" y2="47" stroke="currentColor" strokeWidth="2" opacity="0.5" />
        <line x1="17" y1="32" x2="27" y2="32" stroke="currentColor" strokeWidth="2" opacity="0.5" />
        <line x1="37" y1="32" x2="47" y2="32" stroke="currentColor" strokeWidth="2" opacity="0.5" />
        {/* Diagonal spokes */}
        <line x1="21.4" y1="21.4" x2="27.8" y2="27.8" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
        <line x1="36.2" y1="36.2" x2="42.6" y2="42.6" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
        <line x1="42.6" y1="21.4" x2="36.2" y2="27.8" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
        <line x1="27.8" y1="36.2" x2="21.4" y2="42.6" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      </svg>
      {label && (
        <p className={cn("text-muted-foreground font-medium animate-pulse", labelSizeMap[size])}>
          {label}
        </p>
      )}
    </div>
  );
}
